<template>
    <div
        class="notification-box position-relative"
        role="button"
        data-bs-toggle="dropdown"
        data-bs-auto-close="outside"
        aria-expanded="false"
    >
        <i class="far fa-bell fa-lg"></i>
        <span
            v-if="notificationsCount > 0 && notificationsCount < 11"
            class="badge rounded-pill bg-warning"
        >
            {{ notificationsCount }}
        </span>
        <span
            v-if="notificationsCount > 10"
            class="badge rounded-pill bg-warning"
            :style="{ right: '-15px' }"
        >
            10+
        </span>
    </div>

    <div class="dropdown-menu dropdown-menu-end p-0" style="min-width: 300px">
        <div class="p-2 border-bottom">
            <h5 class="dropdown-header text-center">Notificaciones</h5>
        </div>

        <div class="notification-list" style="max-height: 350px; overflow-y: auto">
            <div v-if="notifications[0]" class="p-2">
                <div
                    v-if="notificationsCount > 0"
                    class="dropdown-item d-flex justify-content-start border-0 p-0"
                >
                    <div role="button" class="mark-read-btn pb-2" @click="markAllAsRead">
                        Marcar todas como leidas
                    </div>
                </div>
                <div
                    v-for="notification in notifications.slice(0, 10)"
                    :key="notification.id"
                    class="dropdown-item py-2 px-2 border mb-1"
                    :class="{ 'bg-light': !notification.read }"
                    :style="{ cursor: 'pointer' }"
                    @click="openTab(notification)"
                >
                    <div class="d-flex justify-content-between align-items-center">
                        <h6 class="mb-0 text-truncate" :title="notification.titulo">
                            {{ notification.titulo }}
                        </h6>
                        <small class="text-muted ms-2">{{ timeAgo(notification.createdAt) }}</small>
                    </div>
                    <p class="mb-1 small">{{ notification.body }}</p>

                    <button
                        v-if="!notification.read"
                        @click.stop="markAsRead(notification)"
                        class="btn btn-sm btn-link p-0"
                    >
                        Marcar como leído
                    </button>
                </div>
                <div v-if="notificationsCount > 10" class="py-2 px-2">
                    <div class="d-flex justify-content-center">
                        <p class="mb-0">
                            Puedes ver el resto de notificaciones no leidas haciendo click en "ver
                            todas"
                        </p>
                    </div>
                </div>
            </div>
            <div v-if="notifications.length === 0" class="dropdown-item text-center my-auto">
                <h6 class="mt-2">No hay notificaciones</h6>
            </div>
        </div>

        <div v-if="notifications[0]" class="dropdown-footer border-top p-2">
            <div class="dropdown-item d-flex justify-content-center mb-0">
                <div role="button" class="view-all-btn" @click="viewAllNotifications">
                    Ver todas
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue';

import useNotifications from '@/services/useNotifications';
import useSocket from '@/services/useSocket';
import { useSnackbar } from 'vue3-snackbar';

import { useStore } from 'vuex';

const snackbar = useSnackbar();

const service = useNotifications();
const store = useStore();
const notifications = ref([]);
const notificationsCount = ref(0);
const audio = new Audio('/audio/notification.mp3');
const originalTitle = document.title;
const originalFavicon = document.querySelector('link[rel="icon"]')?.href || '/favicon.ico';
let faviconInterval = null;

const { socket, addEvent } = useSocket();

function startFaviconBlink() {
    if (faviconInterval) return;

    let isOriginal = true;
    faviconInterval = setInterval(() => {
        const favicon = document.querySelector('link[rel="icon"]');
        if (favicon) {
            favicon.href = isOriginal
                ? 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16"><circle cx="8" cy="8" r="8" fill="%23ff6b6b"/></svg>'
                : originalFavicon;
            isOriginal = !isOriginal;
        }
    }, 1000);
}

function stopFaviconBlink() {
    if (faviconInterval) {
        clearInterval(faviconInterval);
        faviconInterval = null;
        const favicon = document.querySelector('link[rel="icon"]');
        if (favicon) {
            favicon.href = originalFavicon;
        }
    }
}

function updatePageTitle() {
    if (notificationsCount.value > 0) {
        document.title = `(${
            notificationsCount.value > 10 ? '10+' : notificationsCount.value
        }) ${originalTitle}`;
        startFaviconBlink();
    } else {
        document.title = originalTitle;
        stopFaviconBlink();
    }
}

async function setNotifications() {
    await service.getNotificationsByUser(store.getters['Auth/getUserId']);
    notifications.value = service.notifications.value;
    notificationsCount.value = 0;
    // eslint-disable-next-line no-restricted-syntax
    for (const notification of notifications.value) {
        if (!notification.read) {
            notificationsCount.value += 1;
        }
    }
    updatePageTitle();
}

addEvent('rte:Notifications', async ({ action }) => {
    console.log('listener: notification');
    switch (action) {
        case 'add:data':
            try {
                await setNotifications();
                await audio.play();
                snackbar.add({
                    type: 'warning',
                    text: notifications.value[0].body,
                });
            } catch (err) {
                console.log(err);
            }
            break;
        default:
            break;
    }
});

onMounted(async () => {
    await setNotifications();
});

onUnmounted(() => {
    socket.off('rte:LogEditor');
    document.title = originalTitle;
    stopFaviconBlink();
});

function timeAgo(dateString) {
    const now = new Date();
    const past = new Date(dateString);
    const seconds = Math.floor((now - past) / 1000);

    const intervals = {
        año: 31536000,
        mes: 2592000,
        semana: 604800,
        día: 86400,
        hora: 3600,
        minuto: 60,
        segundo: 1,
    };

    // eslint-disable-next-line no-restricted-syntax
    for (const [label, secondsInUnit] of Object.entries(intervals)) {
        const amount = Math.floor(seconds / secondsInUnit);
        if (amount >= 1) {
            return `hace ${amount} ${label}${amount > 1 ? 's' : ''}`;
        }
    }

    return 'justo ahora';
}

const markAsRead = async (notification) => {
    await service.markOneAsRead(notification.id);
    await setNotifications();
};

const markAllAsRead = async () => {
    await service.markAllAsRead(store.getters['Auth/getUserId']);
    await setNotifications();
};

const openTab = async (item) => {
    store.dispatch('ContentManager/openWindow', {
        id: `${item.target}_${item.id}`,
        name: item.tabName,
        component: item.target,
        params: {
            item: { ...item.item },
        },
    });
    markAsRead(item);
};

const viewAllNotifications = async () => {
    store.dispatch('ContentManager/openWindow', {
        id: 'List_notifications',
        name: 'Mis Notificaciones',
        component: 'List',
        params: {
            title: 'Notificaciones',
            columns: [
                {
                    title: 'Título',
                    attr: 'titulo',
                    width: 40,
                },
                {
                    title: 'Fecha',
                    attr: 'createdAt',
                    width: 40,
                    component: 'DateTimeColumn',
                },
                {
                    title: 'Estado',
                    attr: 'read',
                    width: 10,
                    component: 'NotificationStatus',
                },
            ],
            urlQuery: `/notifications/user/${store.getters['Auth/getUserId']}`,
        },
    });
    await setNotifications();
};
</script>

<style scoped>
.dropdown-menu {
    background-color: #ffffff;
    box-shadow: 0.2px 0.2px 3px 1px color(srgb 0 0 0 / 0.18);
}

.dropdown-menu li:last-child {
    border-bottom: 0px !important;
}

i {
    font-size: 1.2rem;
}

.notification-box {
    position: relative;
}

.dropdown-header {
    color: #4b4b4b;
}

.dropdown-item {
    display: block;
    opacity: 1 !important;
    border-radius: 4px;
}

.dropdown-footer .dropdown-item {
    margin-bottom: 0px !important;
    border: none;
}

.dropdown-item h6 {
    font-size: 0.9rem;
    font-weight: 600;
}

.dropdown-item p {
    font-size: 0.85rem;
}

.view-all-btn:hover,
.mark-read-btn:hover {
    cursor: pointer;
    text-decoration: underline;
    color: #0d6efd;
}
</style>
